import { logEvent, setUserProperties } from "firebase/analytics";
import { analytics , db } from "../firebase/firebase.config";
import { doc, getDoc, updateDoc, increment } from 'firebase/firestore';

interface CustomEventParams {
  [key: string]: string | number | boolean | undefined;
}

class AnalyticsService {
  private isEnabled: boolean;

  constructor() {
    // Analytics is enabled if we have analytics instance and not in development
    this.isEnabled = !!analytics && process.env.NODE_ENV === 'production';
  }

  /**
   * Track a page view
   */
  trackPageView(pageName: string, additionalParams?: CustomEventParams) {
    if (!this.isEnabled) return;

    logEvent(analytics!, 'page_view', {
      page_title: pageName,
      page_location: window.location.href,
      page_path: window.location.pathname,
      ...additionalParams,
    });
  }

  /**
   * Track user sign up
   */
  trackSignUp(method: string = 'email') {
    if (!this.isEnabled) return;

    logEvent(analytics!, 'sign_up', {
      method,
    });
  }

  /**
   * Track user login
   */
  trackLogin(method: string = 'email') {
    if (!this.isEnabled) return;

    logEvent(analytics!, 'login', {
      method,
    });
  }

  /**
   * Track when user starts a visa application
   */
  trackVisaApplicationStart(visaType: string) {
    if (!this.isEnabled) return;

    logEvent(analytics!, 'visa_application_start', {
      visa_type: visaType,
      category: 'visa_application',
    });
  }

  /**
   * Track when user completes a visa application step
   */
  trackVisaApplicationStep(visaType: string, step: string) {
    if (!this.isEnabled) return;

    logEvent(analytics!, 'visa_application_step', {
      visa_type: visaType,
      step,
      category: 'visa_application',
    });
  }

  /**
   * Track chat interactions
   */
  trackChatMessage(visaType: string, messageType: 'user' | 'assistant' = 'user') {
    if (!this.isEnabled) return;

    logEvent(analytics!, 'chat_message', {
      visa_type: visaType,
      message_type: messageType,
      category: 'engagement',
    });
  }

  /**
   * Track document uploads
   */
  trackDocumentUpload(visaType: string, documentType: string) {
    if (!this.isEnabled) return;

    logEvent(analytics!, 'document_upload', {
      visa_type: visaType,
      document_type: documentType,
      category: 'document_management',
    });
  }

  /**
   * Track subscription events
   */
  trackSubscriptionEvent(eventType: 'view_plans' | 'start_trial' | 'subscribe' | 'cancel', plan?: string) {
    if (!this.isEnabled) return;

    logEvent(analytics!, 'subscription_event', {
      event_type: eventType,
      plan,
      category: 'subscription',
    });
  }

  /**
   * Track custom events
   */
  trackCustomEvent(eventName: string, parameters?: CustomEventParams) {
    if (!this.isEnabled) return;

    logEvent(analytics!, eventName, parameters);
  }

  /**
   * Track errors
   */
  trackError(error: Error, context?: string) {
    if (!this.isEnabled) return;

    logEvent(analytics!, 'error', {
      error_message: error.message,
      error_stack: error.stack?.substring(0, 500), // Limit stack trace length
      context,
      category: 'error',
    });
  }

  /**
   * Track feature usage
   */
  trackFeatureUsage(feature: string, visaType?: string) {
    if (!this.isEnabled) return;

    logEvent(analytics!, 'feature_usage', {
      feature,
      visa_type: visaType,
      category: 'feature',
    });
  }

  /**
   * Set user properties for analytics
   */
  setUserProperties(properties: { [key: string]: string | number | boolean | undefined }) {
    if (!this.isEnabled) return;

    setUserProperties(analytics!, properties);
  }
}

export const analyticsService = new AnalyticsService();
export default analyticsService;

export interface AnalyticsData {
    numberOfUsers: number;
    personalStatementGenerated: number;
    profileScored: number;
}

export const fetchAnalyticsData = async (): Promise<AnalyticsData | null> => {
    try {
        const analyticsDoc = doc(db, 'analytics', 'analytics');
        const docSnap = await getDoc(analyticsDoc);
        
        if (docSnap.exists()) {
            const data = docSnap.data();
            return {
                numberOfUsers: data.numberOfUsers || 0,
                personalStatementGenerated: data.personalStatementGenerated || 0,
                profileScored: data.profileScored || 0,
            };
        } else {
            console.log('No analytics document found');
            return null;
        }
    } catch (error) {
        console.error('Error fetching analytics data:', error);
        return null;
    }
};

export const incrementProfileScored = async (): Promise<void> => {
    try {
        const analyticsDoc = doc(db, 'analytics', 'analytics');
        await updateDoc(analyticsDoc, {
            profileScored: increment(1)
        });
    } catch (error) {
        console.error('Error incrementing profileScored:', error);
        throw error;
    }
};

export const incrementPersonalStatementGenerated = async (): Promise<void> => {
    try {
        const analyticsDoc = doc(db, 'analytics', 'analytics');
        await updateDoc(analyticsDoc, {
            personalStatementGenerated: increment(1)
        });
    } catch (error) {
        console.error('Error incrementing personalStatementGenerated:', error);
        throw error;
    }
}; 